import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	ElementRef,
	Injector,
	ViewChild,
	afterNextRender,
	computed,
	effect,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { UNTITLED_PAGE_LABEL, type PageEditorPage } from '../page-editor.model';

const SCROLL_THRESHOLD_PX = 2;
const SCROLL_STEP_RATIO = 0.75;
const MOBILE_BREAKPOINT_PX = 768;
const MOBILE_PANEL_TRIGGER_GAP_PX = 8;
const MOBILE_PANEL_VIEWPORT_INSET_PX = 16;

@Component({
	selector: 'jepto-page-editor-tabs',
	standalone: true,
	imports: [CommonModule, MatIconModule],
	templateUrl: './jepto-page-editor-tabs.component.html',
	styleUrl: './jepto-page-editor-tabs.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JeptoPageEditorTabsComponent {
	/** The tab row is destroyed and recreated when crossing the mobile breakpoint, so observe each new element. */
	@ViewChild('tabsScrollHost')
	private set scrollHostRef(ref: ElementRef<HTMLElement> | undefined) {
		const el = ref?.nativeElement;
		if (el === this.scrollHost) return;
		this.stopObservingScrollHost?.();
		this.stopObservingScrollHost = undefined;
		this.scrollHost = el;
		if (el) this.observeScrollHost(el);
	}

	private scrollHost?: HTMLElement;
	private stopObservingScrollHost?: () => void;

	/** Tab items (label and optional icon). */
	readonly tabs = input.required<PageEditorPage[]>();
	/** Index of the currently selected tab (0-based). */
	readonly selectedIndex = input<number>(0);
	/** When true, view mode: mobile dropdown trigger goes edge-to-edge (no horizontal padding). */
	readonly viewOnly = input<boolean>(false);
	/** When true and viewport is mobile, open the dropdown on init (e.g. for demos). */
	readonly initialDropdownOpen = input<boolean>(false);
	/** Optional data-testid for the root element. */
	readonly testId = input<string | null>(null);
	/** Accessibility label for the tablist. */
	readonly ariaLabel = input<string>('Pages');

	/** Emitted when the user selects a different tab. */
	readonly tabChange = output<number>();

	// fallow-ignore-next-line unused-class-member
	readonly selectedPage = computed(() => {
		const list = this.tabs();
		const idx = this.selectedIndex();
		return list[idx] ?? null;
	});

	/** True when content is scrolled so more content exists to the left. */
	readonly canScrollLeft = signal(false);
	/** True when content is scrolled so more content exists to the right. */
	readonly canScrollRight = signal(false);

	/** True when viewport is at or below mobile breakpoint (768px). Shows dropdown instead of horizontal tabs. */
	readonly isMobile = signal(false);
	/** Mobile dropdown open state. */
	readonly dropdownOpen = signal(false);
	/** Position for the mobile panel when open (fixed positioning so it is not clipped). */
	readonly panelPosition = signal<{
		top: number;
		left: number;
		width: number;
	} | null>(null);

	@ViewChild('mobileTrigger')
	private mobileTriggerRef?: ElementRef<HTMLElement>;

	private destroyRef = inject(DestroyRef);
	private injector = inject(Injector);
	private initialOpenApplied = false;

	constructor() {
		afterNextRender(() => this.observeMobileBreakpoint(), {
			injector: this.injector,
		});
		this.destroyRef.onDestroy(() => this.stopObservingScrollHost?.());
		effect(() => {
			this.tabs();
			// Run after layout so scrollWidth is correct when tabs are added/removed
			requestAnimationFrame(() => this.updateScrollState());
		});
		effect(() => {
			if (
				this.isMobile() &&
				this.initialDropdownOpen() &&
				!this.initialOpenApplied
			) {
				this.initialOpenApplied = true;
				requestAnimationFrame(() => this.applyInitialDropdownOpen());
			}
		});
	}

	private applyInitialDropdownOpen(): void {
		const el = this.mobileTriggerRef?.nativeElement;
		if (!el) return;
		this.panelPosition.set(this.#computeMobilePanelPosition(el));
		this.dropdownOpen.set(true);
	}

	private observeMobileBreakpoint(): void {
		const query = window.matchMedia(
			`(max-width: ${MOBILE_BREAKPOINT_PX}px)`
		);
		const update = () => this.isMobile.set(query.matches);
		update();
		query.addEventListener('change', update);
		this.destroyRef.onDestroy(() =>
			query.removeEventListener('change', update)
		);
	}

	toggleDropdown(): void {
		const willOpen = !this.dropdownOpen();
		if (willOpen) {
			const el = this.mobileTriggerRef?.nativeElement;
			if (el) {
				this.panelPosition.set(this.#computeMobilePanelPosition(el));
			}
			this.dropdownOpen.set(true);
		} else {
			this.dropdownOpen.set(false);
			this.panelPosition.set(null);
		}
	}

	closeDropdown(): void {
		this.dropdownOpen.set(false);
		this.panelPosition.set(null);
	}

	selectPageAndClose(index: number): void {
		this.tabChange.emit(index);
		this.closeDropdown();
	}

	private observeScrollHost(el: HTMLElement): void {
		const update = () => this.updateScrollState();
		// Measure after layout; the query setter runs during change detection
		const frame = requestAnimationFrame(update);

		const resizeObserver = new ResizeObserver(update);
		resizeObserver.observe(el);

		this.stopObservingScrollHost = () => {
			cancelAnimationFrame(frame);
			resizeObserver.disconnect();
		};
	}

	onScroll(): void {
		this.updateScrollState();
	}

	updateScrollState(): void {
		const el = this.scrollHost;
		if (!el) return;

		const { scrollLeft, scrollWidth, clientWidth } = el;
		const canLeft = scrollLeft > SCROLL_THRESHOLD_PX;
		const canRight =
			scrollWidth > clientWidth &&
			scrollLeft < scrollWidth - clientWidth - SCROLL_THRESHOLD_PX;

		this.canScrollLeft.set(canLeft);
		this.canScrollRight.set(canRight);
	}

	scrollLeft(): void {
		const el = this.scrollHost;
		if (!el) return;
		const step = Math.max(el.clientWidth * SCROLL_STEP_RATIO, 100);
		this.scrollByStep(el, -step);
	}

	scrollRight(): void {
		const el = this.scrollHost;
		if (!el) return;
		const step = Math.max(el.clientWidth * SCROLL_STEP_RATIO, 100);
		this.scrollByStep(el, step);
	}

	/** Animated scroll (like Material's tab pagination); the (scroll) handler keeps the arrows in sync. */
	private scrollByStep(el: HTMLElement, left: number): void {
		const reduceMotion =
			typeof matchMedia === 'function' &&
			matchMedia('(prefers-reduced-motion: reduce)').matches;
		el.scrollBy({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
	}

	onTabChange(index: number): void {
		this.tabChange.emit(index);
	}

	displayLabel(tab: PageEditorPage): string {
		const trimmed = tab.label?.trim();
		return trimmed ? trimmed : UNTITLED_PAGE_LABEL;
	}

	trackByPageId(_index: number, page: PageEditorPage): string {
		return page.id;
	}

	#computeMobilePanelPosition(trigger: HTMLElement): {
		top: number;
		left: number;
		width: number;
	} {
		const rect = trigger.getBoundingClientRect();
		if (this.viewOnly()) {
			return {
				top: rect.bottom + MOBILE_PANEL_TRIGGER_GAP_PX,
				left: rect.left,
				width: rect.width,
			};
		}

		const inset = MOBILE_PANEL_VIEWPORT_INSET_PX;
		const viewportWidth = window.innerWidth;
		return {
			top: rect.bottom + MOBILE_PANEL_TRIGGER_GAP_PX,
			left: inset,
			width: Math.max(rect.width, viewportWidth - inset * 2),
		};
	}
}
